import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}
import translate from "@insite/client-framework/Translate";

const mapStateToProps = (state: ApplicationState) => ({
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderHistoryPaginationStyles {
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    resultCount?: InjectableCss;
}

export const orderHistoryPaginationStyles: OrderHistoryPaginationStyles = {
    wrapper: {
        css: css`
            margin-top: .625rem;
            color: ${themeColors.colorCharcoalGrey};
            font-size: ${themeFontSizes.size14};
            letter-spacing: 0;
            display: flex;
            padding: 10px 0;
            margin-bottom: 0;
            margin-top: 5px;
            border-top: 1px solid #E4E8F0;
            border-bottom: 1px solid #E4E8F0;
            align-items: center;
            z-index: 1;
            position: relative;

            .order-paging {
                margin: 0;
                width: auto;
                justify-content: flex-start;
                padding-left: 25px;

                > span {
                    label {
                        color: #333F48;
                        font-family: 'Roboto', sans-serif;
                        font-size: 14px;
                        font-weight: 500;
                        letter-spacing: 0;
                        padding: 10px 15px 0 0;
                    }
                }

                select[data-test-selector="paginationPerPageSelect"] {
                    min-width: 79px;
                    border-color: #C2D1D9 !important;
                    opacity: .93;
                    background-color: #fff !important;
                    color: #161616 !important;
                    letter-spacing: 0.16px;
                }
            }

            nav {
                display: flex;
                justify-content: center;

                @media screen and (min-width: 1280px) {
                    width: 70%;
                    margin-top: 0;
                    ul.pagination-arrow li:nth-child(3) {
                        margin-left: 55px;
                    }

                    ul.pagination-arrow li:nth-last-child(2) {
                        margin-left: 55px;
                    }
                }

                @media screen and (min-width: 1199px) {
                    margin-top: 0;
                    margin-left: 60px;
                }

                @media screen and (min-width: 1400px) {
                    margin-left: 110px;
                }
            }

            @media screen and (max-width: 1279px) {
                nav {
                    flex-basis: 100%;
                    max-width: 100%;
                }
                .order-paging {
                    width: 100%;
                    flex-basis: 100%;
                    padding-left: 0;
                    flex-wrap: wrap;
                    > span {
                        > div {
                            flex-direction: column;
                        }
                        label {
                            text-align: left;
                            line-height: 31px;
                            padding: 0 15px 0 0;

                            + div {
                                min-width: 149px;
                            }
                        }
                    }
                }
                
                .order-result-count {
                    font-weight: 600;
                    position: absolute;
                    right: 0;
                    width: auto;
                    top: 49px;
                }
            }

        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
    resultCount: {
        css: css`
            display: flex;
            width: auto;
            color: #535E64;
            font-family: 'Lato',sans-serif;
            font-size: 14px;
            font-style: italic;
            letter-spacing: 0;
            line-height: 31px;

            span {
                color: #535E64;
                display: inline-block;
                padding-right: 5px;
            }

            @media screen and (min-width: 1280px) {
                width: auto;
            }
        `,
    },
};

const styles = orderHistoryPaginationStyles;

const OrderHistoryPagination: FC<Props> = ({ isVmiOrderHistoryPage, updateSearchFields }) => {
    const ordersDataView = useContext(OrdersDataViewContext);

    const pageSizeCookieName = isVmiOrderHistoryPage ? "VmiOrderHistory-PageSize" : "OrderHistory-PageSize";

    if (!ordersDataView.value || !ordersDataView.pagination) {
        return null;
    }

    const { totalItemCount, page, pageSize, pageSizeOptions } = ordersDataView.pagination;

    if (totalItemCount === 0) {
        return null;
    }

    const changePage = (newPageIndex: number) => {
        updateSearchFields({
            page: newPageIndex,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    const itemsText = (totalItemCount !== 1 ? " Results" : " Result");
    const pageSizeNew = (totalItemCount < pageSize ? totalItemCount : pageSize ); 

    return (
        <Typography as="div" {...styles.wrapper}>
        <StyledWrapper {...styles.resultCount} className="order-result-count"  data-test-selector="orderHistory_resultCount">
            <Typography {...styles.itemsText}>Showing </Typography>
            <Typography {...styles.countText} data-test-selector="orderHistory_CountText">
                {pageSizeNew}
            </Typography>
            <Typography {...styles.itemsText}> of </Typography>
            <Typography {...styles.countText} data-test-selector="orderHistory_CountText">
                {totalItemCount}
            </Typography>
            <Typography {...styles.itemsText}>{translate(itemsText)}</Typography>
        </StyledWrapper>

        <Pagination
            {...styles.pagination}
            resultsCount={totalItemCount}
            currentPage={page}
            resultsPerPage={pageSize}
            resultsPerPageOptions={pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie={pageSizeCookieName}
            className="order-paging"
        />
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderHistoryPagination),
    definition: {
        displayName: "Pagination",
        group: "Order History",
        allowedContexts: ["OrderHistoryPage", "VmiOrderHistoryPage"],
    },
};

export default widgetModule;
